// GFMP-Kandake — Duration utilities.
// Canonical storage is seconds (number). UI displays MM:SS by default.
// Input parsing accepts either raw seconds (e.g. "222", "222.5") or MM:SS (e.g. "3:42").

/**
 * Format a numeric seconds value as MM:SS for display.
 * Returns "—" for null / empty / NaN values.
 * Sub-second precision is dropped — seconds are rounded to the nearest int for display.
 */
export function formatDurationMMSS(secs) {
  if (secs == null || secs === "") return "—";
  const n = Number(secs);
  if (!Number.isFinite(n) || n < 0) return "—";
  const total = Math.round(n);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Parse a user-entered duration string into seconds.
 * Accepts:
 *   - "222"      -> 222
 *   - "222.5"    -> 222.5
 *   - "3:42"     -> 222
 *   - "3:42.5"   -> 222.5
 *   - "1:03:42"  -> 3822  (HH:MM:SS — defensive, not advertised)
 *   - ""         -> null
 *   - null       -> null
 * Returns a finite number of seconds (>= 0), or null if invalid/empty.
 */
export function parseDurationInput(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (trimmed === "") return null;

  if (trimmed.includes(":")) {
    const parts = trimmed.split(":").map((p) => p.trim());
    if (parts.some((p) => p === "" || Number.isNaN(Number(p)))) return null;
    const nums = parts.map(Number);
    let total = 0;
    if (nums.length === 2) {
      // MM:SS
      const [m, s] = nums;
      if (m < 0 || s < 0 || s >= 60) return null;
      total = m * 60 + s;
    } else if (nums.length === 3) {
      // HH:MM:SS
      const [h, m, s] = nums;
      if (h < 0 || m < 0 || s < 0 || m >= 60 || s >= 60) return null;
      total = h * 3600 + m * 60 + s;
    } else {
      return null;
    }
    if (!Number.isFinite(total) || total < 0) return null;
    return total;
  }

  const raw = Number(trimmed);
  if (!Number.isFinite(raw) || raw < 0) return null;
  return raw;
}

/**
 * Check whether a user-entered string is a valid duration (MM:SS or raw seconds).
 * Empty string is considered "not set" (returns true so optional fields don't error).
 */
export function isValidDurationInput(value) {
  if (value == null) return true;
  const trimmed = String(value).trim();
  if (trimmed === "") return true;
  return parseDurationInput(trimmed) !== null;
}
